import { useEffect, useState } from "react";
import { getDataset, type DatasetDescription } from "@/api/datasets";
import { ConfirmDialog } from "@/components/ui";
import { formatList, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { wallMessages } from "@/i18n/messages/wall";
import { frameProjectId } from "@/lib/frame-project";
import { useWallFrame } from "./wall-frame-source";

/**
 * Delete's confirmation for the items a menu targets. One Data frame names
 * its rows and the charts that stop updating, read from its table once the
 * dialog opens; until then, and for anything else, the usual words show.
 */
export function WallDeleteConfirm({
  frameIds,
  onConfirm,
  onCancel,
}: {
  frameIds: readonly string[];
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT(wallMessages);
  const tCommon = useT(commonMessages);
  const tData = useT(framesDatasetMessages);
  const one = frameIds.length === 1 ? frameIds[0] : undefined;
  const frame = useWallFrame(one);
  const table = frame?.frameType === "dataset" ? frame : undefined;
  const tableId = table?.frameId;
  const projectId = table ? frameProjectId(table) : null;
  const [described, setDescribed] = useState<DatasetDescription | null>(null);

  useEffect(() => {
    if (!tableId || !projectId) return;
    let current = true;
    getDataset(projectId, tableId).then(
      (description) => {
        if (current) setDescribed(description);
      },
      // The usual words stay: deleting does not wait on the table.
      () => {},
    );
    return () => {
      current = false;
    };
  }, [projectId, tableId]);

  const data = described && described.frame_id === one ? described : null;
  const consumers = data?.consumers.map((consumer) => consumer.title) ?? [];
  const usual =
    frameIds.length > 1 ? t("deleteItemsBody") : t("deleteItemBody");
  return (
    <ConfirmDialog
      title={
        data
          ? tData("deleteTitle", { title: data.title, count: data.row_count })
          : frameIds.length > 1
            ? t("deleteItemsTitle", { count: frameIds.length })
            : t("deleteItemTitle")
      }
      description={
        consumers.length > 0
          ? tData("deleteConsumers", {
              names: formatList(consumers),
              count: consumers.length,
            })
          : usual
      }
      confirmLabel={tCommon("delete")}
      onConfirm={onConfirm}
      onCancel={onCancel}
      data-testid="wall-card-delete-confirm"
    />
  );
}
